/**
 * Cancellation Error class
 *
 * Why:Distinguish cancelled requests from other errors
 * What: Error class for intentially cancelled request
 *
 * Benefit:
 *      - No error UI for intential cancellation
 *      - Clean component unmount handling
 *      -Proper request lifecycle managment
 *
 * Use Cases:
 *      - Component unmounts before request completes
 *      - User navigates away
 *      - User cancel logn-running operation
 *      - Debounced/throttled request superseded
 */

import { AppError, type AppErrorOptions } from "./base/AppError";
import { ErrorCategory, ErrorSeverity } from "../types";
import { ERROR_CODES, ERROR_MESSAGES } from "../constants";

/**
 * Cancellation reason types
 */
export const CancellationReason = {
  /** Component unmounted */
  COMPONENT_UNMOUNT: "component_unmount",
  /** User navigation */
  NAVIGATION: "navigation",
  USER_CANCELLED: "user_cancelled",
  /** Superseded by newer request */
  SUPERSEDED: "superseded",
  TIMEOUT: "timeout",
  /** Unknown reason */
  UNKNOWN: "unknown",
} as const;

export type CancellationReason =
  (typeof CancellationReason)[keyof typeof CancellationReason];

/**
 * Options specific to cancellation errors
 */
export interface CancellationErrorOptions extends Omit<
  AppErrorOptions,
  "statusCode"
> {
  // Reason for cancellation
  reason?: CancellationReason;
  // ID of the cancelled request
  requestId?: string;
}

/**
 * Cancellation Error
 *
 * Represents intentially cancelled requests.
 * These are not true errors and should not be displayed to users.
 */
export class CancellationError extends AppError {
  public readonly reason: CancellationReason;
  public readonly requestId?: string;

  constructor(
    message: string = ERROR_MESSAGES.NETWORK_CANCELLED,
    options: CancellationErrorOptions = {},
  ) {
    super(message, {
      statusCode: 0,
      code: options.code ?? ERROR_CODES.NETWORK_CANCELLED,
      category: ErrorCategory.CANCELLED,
      severity: ErrorSeverity.LOW, // Cancellation are low severity
      isOperational: true,
      isRetryable: false,
      context: options.context,
      cause: options.cause,
    });

    this.reason = options.reason ?? CancellationReason.UNKNOWN;
    this.requestId = options.requestId;
  }

  /**
   * Create for component unmount
   *
   */
  static componentUnmount(requestId?: string): CancellationError {
    return new CancellationError("Request cancelled: component unmount", {
      reason: CancellationReason.COMPONENT_UNMOUNT,
      requestId,
    });
  }

  /**
   * Create for user navigation
   */
  static navigation(requestId?: string): CancellationError {
    return new CancellationError("Request cancelled: user navigated away", {
      reason: CancellationReason.NAVIGATION,
      requestId,
    });
  }

  /**
   * Create for user explicit cancellation
   */
  static userCancelled(requestId?: string): CancellationError {
    return new CancellationError("Request cancelled by user", {
      reason: CancellationReason.USER_CANCELLED,
      requestId,
    });
  }

  /**
   * Create for seperseded request
   */
  static superseded(requestId?: string): CancellationError {
    return new CancellationError(
      "Request cancelled: superseded by newer request",
      {
        reason: CancellationReason.SUPERSEDED,
        requestId,
      },
    );
  }

  /**
   * Check if this is a user-initialized cancellation
   */
  public isUserInitiated(): boolean {
    return this.reason === CancellationReason.USER_CANCELLED;
  }

  /**
   * Check if error should be silently ifnored
   * Most cancellation should not show error UI
   */
  public shouldIgnore(): boolean {
    // only show error for user-initiated cancellation (they clicked cancel)
    return this.reason !== CancellationReason.USER_CANCELLED;
  }
}
